import axios from 'axios';
import Immutable from 'immutable';
import { reportAPIErrorToSentry, catchBlockErrors } from './utils/sentry';
import { showSnackbar } from './stores/app_s';
import {
    adaptResolved,
    adaptLegend,
    adaptConfigForSave,
} from './thresholdAdapter';

/*
# API Module Documentation

The `API` module is a utility class designed to handle API interactions in a React application. It provides methods for making HTTP requests, managing authentication tokens, handling file uploads/downloads, and dispatching Redux actions based on API responses. The module is built on top of the `axios` library and integrates with Redux for state management.

---

## Table of Contents

1. [Overview](#overview)
2. [Constructor](#constructor)
3. [Endpoints](#endpoints)
4. [Methods](#methods)
   - [setToken](#settoken)
   - [call](#call)
   - [subscribe](#subscribe)
   - [filesUpload](#filesupload)
   - [fileUpload](#fileupload)
   - [photoUpload](#photoupload)
   - [fileDownload](#filedownload)
   - [nonIM](#nonim)
   - [getToken](#gettoken)
   - [Private Methods](#private-methods)
5. [Error Handling](#error-handling)
6. [Usage](#usage)

---

## Overview

The `API` module is designed to simplify API interactions by providing a consistent interface for making requests, handling errors, and managing state updates. It supports:

- Basic API calls (`GET`, `POST`, etc.)
- File uploads and downloads
- Redux state updates via `subscribe`
- Error reporting to Sentry
- Token-based authentication

---

## Constructor

### `API(dispatch)`

The constructor initializes the `API` instance.

- **Parameters:**
  - `dispatch` (function): The Redux `dispatch` function for dispatching actions.

- **Properties:**
  - `dispatch`: Stores the Redux `dispatch` function.
  - `pendingCalls`: An `Immutable.List` to track pending API calls.
  - `runningRepeat`: A flag to indicate if a repeat call is in progress.
  - `endpoints`: A mapping of endpoint keys to their respective API paths.

---

## Endpoints

The `endpoints` property is a dictionary that maps logical endpoint keys to their corresponding API paths. These keys are used to reference specific API endpoints throughout the application.

Example:
```javascript
"OR/get_organizations": "get_organizations",
"UA/user_info": "get_user_info",
"SL/reportMapData": "report_map",
```

---

## Methods

### `setToken(token)`

Sets the authentication token for API requests.

- **Parameters:**
  - `token` (string): The authentication token.

- **Returns:** The token.

---

### `call({ endpoint, method, options })`

Makes a basic API call without updating Redux state.

- **Parameters:**
  - `endpoint` (string): The API endpoint.
  - `method` (string): The HTTP method (`GET`, `POST`, etc.).
  - `options` (object): Additional options for the request (e.g., headers, data).

- **Returns:** The API response.

---

### `subscribe(callConfig, callbackAction)`

Makes an API call and dispatches a Redux action with the response.

- **Parameters:**
  - `callConfig` (object): Configuration for the API call.
  - `callbackAction` (function): A Redux action creator to handle the response.

- **Returns:** The API response.

---

### `filesUpload({ endpoint, options, data, callbackAction })`

Uploads multiple files to the server.

- **Parameters:**
  - `endpoint` (string): The API endpoint.
  - `options` (object): Options for the request, including the `files` array.
  - `data` (object): Additional data to include in the request.
  - `callbackAction` (function): A Redux action creator to handle the response.

- **Returns:** The API response.

---

### `fileUpload({ endpoint, options, data, callbackAction })`

Uploads a single file to the server.

- **Parameters:**
  - `endpoint` (string): The API endpoint.
  - `options` (object): Options for the request, including the `file` object.
  - `data` (object): Additional data to include in the request.
  - `callbackAction` (function): A Redux action creator to handle the response.

- **Returns:** The API response.

---

### `photoUpload({ endpoint, options, data, callbackAction })`

Uploads an image using `FormData`.

- **Parameters:**
  - `endpoint` (string): The API endpoint.
  - `options` (object): Options for the request.
  - `data` (array): An array of key-value pairs to include in the `FormData`.
  - `callbackAction` (function): A Redux action creator to handle the response.

- **Returns:** The API response.

---

### `fileDownload(callConfig, callbackAction)`

Downloads a file and retrieves the response headers.

- **Parameters:**
  - `callConfig` (object): Configuration for the API call.
  - `callbackAction` (function): A Redux action creator to handle the response.

- **Returns:** An object containing the response data and headers.

---

### `nonIM(callConfig, callbackAction)`

Makes an API call without converting the response to an `Immutable` object.

- **Parameters:**
  - `callConfig` (object): Configuration for the API call.
  - `callbackAction` (function): A Redux action creator to handle the response.

- **Returns:** The API response.

---

### `getToken()`

Retrieves the stored authentication token.

- **Returns:** The authentication token, or `false` if not set.

---

## Private Methods

### `_setConfig({ endpoint, method, options })`

Prepares the configuration object for an `axios` request.

- **Parameters:**
  - `endpoint` (string): The API endpoint.
  - `method` (string): The HTTP method.
  - `options` (object): Additional options for the request.

- **Returns:** The configuration object.

---

### `_axios({ config, callbackAction, immutableResponse })`

Executes an `axios` request and handles the response.

- **Parameters:**
  - `config` (object): The `axios` configuration object.
  - `callbackAction` (function): A Redux action creator to handle the response.
  - `immutableResponse` (boolean): Whether to convert the response to an `Immutable` object.

- **Returns:** The API response.

---

### `_handleSuccess({ data, callbackAction, immutableResponse })`

Handles successful API responses.

- **Parameters:**
  - `data` (object): The response data.
  - `callbackAction` (function): A Redux action creator to handle the response.
  - `immutableResponse` (boolean): Whether to convert the response to an `Immutable` object.

- **Returns:** The processed response data.

---

### `_handleError({ error, callbackAction })`

Handles API errors and reports them to Sentry.

- **Parameters:**
  - `error` (object): The error object.
  - `callbackAction` (function): A Redux action creator to handle the error.

- **Returns:** An object containing the error.

---

### `_displaySnackBarFeedback(notification)`

Displays a snackbar notification.

- **Parameters:**
  - `notification` (object): The notification details (message, variant, etc.).

---

## Error Handling

The module includes robust error handling:

- Reports errors to Sentry using `reportAPIErrorToSentry`.
- Displays user-friendly error messages via a snackbar.
- Handles token expiration by redirecting to the login page.

---

## Usage

### Example: Basic API Call
```javascript
const api = new API(dispatch);
api.setToken("your-auth-token");

api.call({
  endpoint: api.endpoints["OR/get_organizations"],
  method: "GET",
}).then(response => {
  console.log(response);
});
```

### Example: File Upload
```javascript
const file = new File(["content"], "example.txt");
api.fileUpload({
  endpoint: api.endpoints["FE/image_upload"],
  options: { file },
});
```

### Example: Redux Integration
```javascript
api.subscribe(
  { endpoint: api.endpoints["UA/user_info"], method: "GET" },
  userInfoActionCreator
);
```

---
*/

const baseURL = process.env.REACT_APP_SPEC_RITE_URL || '';

// QA-threshold endpoints routed through the FE↔BE adapter in _realThresholdCall.
const THRESHOLD_ENDPOINTS = {
    get_qa_thresholds: 'get',
    update_qa_thresholds: 'update',
    reset_qa_thresholds: 'reset',
    get_threshold_legend: 'legend',
};

function API(dispatch) {
    this.dispatch = dispatch;
    this.pendingCalls = Immutable.List([]);
    this.runningRepeat = false;
    this.endpoints = {
        // Organizations
        'OR/get_organizations': 'get_organizations',
        'OR/timezone': 'timezone',
        'OR/updateTimezone': '/org/update/timezone',
        'OR/updateDisplayName': '/org/update/display_name',
        'OR/unitOptions': '/get_unit_options',
        'OR/updateUnitType': '/update_unit_value',
        'OR/updateSkyviewMaterialPrefs': '/update_skyview_material_prefs',
        // User and Admin
        'UA/user_info': 'get_user_info',
        'UA/getUsers': 'users',
        'UA/invite_user': 'invite_user',
        'UA/reinvite_user': 'reinvite_user',
        'UA/changeOrganization': 'change_own_organization',
        create_org: 'create_org',
        get_equipment_ids: 'get_equipment_ids',
        get_org_locations: 'get_org_locations',
        migrate_equipment: 'migrate_equipment',
        add_equipment: 'add_equipment',
        // Feature Related
        'FE/image_upload': 'feature_image_upload',
        // Visual Assessment
        'VA/create': 'visual_assessment/create',
        'VA/info': 'visual_assessment_info',
        'VA/past': 'visual_assessments',
        'VA/update': 'visual_assessment/update_value',
        // 'VA/review': 'visual_assessment/update/reviewed',
        // Skipline
        bboxMileMarkers: 'bounded_milemarkers',
        'SL/availableCategories': 'available_categories',
        'SL/categoryProperties': 'category_properties',
        'SL/comments': 'comments',
        'SL/DBSummary': 'dashboard_summary',
        'SL/equipment': 'equipment',
        'SL/equipmentNames': 'equipment_available',
        'SL/equipmentUpdateWaze': 'equipment/update/share_location',
        'SL/get_layer_types': 'layer_types',
        'SL/submission_recipient_orgs': 'submission_recipient_orgs',
        'SL/get_segmentation_types': 'segmentation_types',
        'SL/reportNames': 'get_submitted_report_layer_names',
        'SL/reportMapData': 'report_map',
        'SL/reportTableData': 'report_table',
        'SL/report_submission_history': 'report_submission_history',
        'SL/submit_report': 'submit_report',
        'SL/submittedLayerAvailableCategories':
            'get_submitted_report_available_categories',
        'SL/submittedLayerCategoryProperties':
            'get_submitted_report_category_properties',
        'SL/submittedLayerGeoJson': 'get_submitted_report_layer_geojson',
        'SL/sourceOrganizations':
            'get_submitted_report_layer_source_organizations',
        'SL/jobs': 'jobs',
        'SL/jobMetaData': 'job_metadata',
        'SL/mileMarkers': 'milemarkers',
        'SL/orgInfo': 'org_info',
        'SL/report': 'report',
        'SL/report_history': 'report_history',
        'SL/summary': 'summary',
        'SL/templates': 'templates',
        'SL/template': 'template',
        'SL/truckTypes': 'truck_types',
        'SL/userInfo': 'user_info',
        'SL/userUpdate': 'user/update',
        'SL/yearlySummary': 'yearly',
        'IC/allContacts': 'all_intercom_contacts',
        retroFileUpload: 'upload_retroreflectivity_file',
        'SL/updateEquipmentValue': 'equipment/update',

        // retroJobs: "retroreflectivity_jobs",
        uploadRetroFile: 'upload_retro',
        retroMapData: 'retro_summaries',

        retroGEOJSON: 'retroreflectivity_summary',

        segmentationTypes: 'segmentation_types',
        'retro/equipment': 'retroreflectivity_equipment',
        'retro/categories': 'available_retroreflectivity_categories',
        'retro/properties': 'retroreflectivity_category_properties',

        create_or_edit_template: 'custom_template',
        all_templates: 'templates',
        // 'all_templates': 'custom_templates',
        get_template: 'custom_template',
        all_jobs: 'all_jobs',

        uploadRetroFiles: 'upload_retro',
        retroFilenames: 'retro_filenames',
        eagleeye_data: 'eagleeye_data',
        mergeJobs: 'jobs/merge',
        jobDetails: 'job_detail',
        unmerge: 'jobs/unmerge',
        updateJobName: 'jobs/rename',
        mergeImpact: 'merge_impact',
        truckSearch: 'equipment_lookup',
        sharedOrgs: 'shared_orgs',
        shareJob: 'share_job',
        retroRegions: 'retro_regions',

        retroRoutes: 'retro_routes',
        retroJobs: 'retro_all_jobs',

        retroSummaries: 'retro_summaries',
        retro_point: 'retro_point',
        retroData: 'retro_data_window',
        allSharedJobs: 'all_shared_jobs',
        allSharedReport: 'shared_report_pagination',
        createSharedReport: 'shared_report',
        createJob: 'add_scheduled_job',
        allScheduledJobs: 'all_scheduled_jobs',
        deleteJob: 'delete_scheduled_job',
        editScheduledJob: 'jobs/reschedule',
        unshareJob: 'unshare_job',
        toggleJobSharing: 'toggle_job_sharing',
        allRetroJobs: 'retro_all_jobs',
        retroJobsSky: 'retro_jobs',
        retroUpload: 'retro_upload',
        retroFlag: 'set_org_retro',
        userStats: 'user_stats',

        handwork: 'handwork',
        handworkProperties: 'handwork_properties',

        templateShareOrgs: 'template_sharing_orgs',
        shareTemplate: 'set_template_shared',
        createRetroTemplate: 'retro_custom_template',
        allRetroTemplates: 'retro_templates',
        deleteRetroJob: 'delete_retro',
        sharedEquipment: 'shared_equipment',
        allRetroReports: 'retro_report_pagination',
        retroReport: 'retro_report',

        // -== Truck Designer Endpoints ==-

        // Truck Types

        // Truck Carriage
        'TD/readTruckCarriage': 'get_carriage_layout',
        'TD/updateTruckCarriage': 'save_carriage_layout',

        orgUsers: 'org_users',
        crew: 'crew',

        splitJob: 'split_job',
        skyviewFilters: 'get_layer_dropdowns',
        getAllLayers: 'get_user_layers',
        updateLayers: 'update_user_layers',
        getQAThresholds: 'get_qa_thresholds',
        updateQAThresholds: 'update_qa_thresholds',
        resetQAThresholds: 'reset_qa_thresholds',
        getThresholdLegend: 'get_threshold_legend',
        // Granular threshold ops (address one band/sub-mode/override, echo only
        // the delta). NOT in THRESHOLD_ENDPOINTS, so they bypass the resolved
        // GET/update adapter in _realThresholdCall and send backend keys inline.
        qaThresholdBand: 'qa_threshold_band',
        qaThresholdSubMode: 'qa_threshold_sub_mode',
        qaThresholdOverride: 'qa_threshold_override',
        fastSkyviewFilters: 'get_speedy_dropdowns',
        retroFilters: 'retro_filters',
        toggleTruckStatus: 'toggle_active',

        featureOptions: 'get_truck_feature_options',
        getTruckActivities: 'get_truck_activities',
        addTruckActivity: 'add_truck_activity',
        getSwitchboards: 'get_truck_switchboards',
        saveSwitchboard: 'save_truck_switchboard',
        // Condor
        getTrucks: 'get_trucks',
        'TD/readTruckTypes': 'get_truck_types',
        'TD/readTruckFeatureOptions': 'get_truck_feature_options',
        'TD/readTruckItemOptions': 'get_truck_item_options',
        'TD/createTruck': 'add_truck',
        'TD/readTrucks': 'get_trucks',
        'TD/updateTruck': 'update_truck',
        'TD/copyTruck': 'copy_truck',
        'TD/readTruckSwitchboards': 'get_truck_switchboards',
        'TD/updateTruckSwitchboard': 'save_truck_switchboard',
        'TD/readTruckActivities': 'get_truck_activities',
        'TD/createTruckActivity': 'add_truck_activity',
        'TD/editTruckComment': 'edit_truck_comment',
        'TD/saveCommentDraft': 'save_comment_draft',
        'TD/getCommentDraft': 'get_comment_draft',
        'TD/uploadTruckFile': 'upload_truck_file',
        truckSubmit: 'submit',
        // Truck Activities

        // Truck Switchboard

        // Truck Hardware
        // 'TD/createTruckItem': 'add_truck_item',
        // 'TD/readTruckItems': 'get_truck_items',
        // 'TD/updateTruckItems': 'save_truck_items',

        // // Truck Hardware Config
        // 'TD/readTruckItemConfig': 'get_truck_item_config',
        //

        renameRetro: 'retro_jobs/rename',
        // AI ENDPOINTs
        meetingLocation: 'job_dashboard',

        // Job Dashboard - Line Types / Progress Trackers
        'JD/getLineTypes': 'job_dashboard/line_types',
        'JD/getProgressTrackers': 'job_dashboard',
        'JD/addOrUpdateProgressTracker': 'job_dashboard',
        'JD/deleteProgressTracker': 'job_dashboard',

        // Dashboard
        getDashboardSettings: 'get_dashboard_settings',
        updateDashboardSettings: 'update_dashboard_settings',
    };
}

API.prototype.setToken = function (token) {
    return (this.token = token);
};

// Real (live-backend) threshold calls, routed through the FE↔BE adapter so the
// rest of the FE sees its own shape. Returns the adapted Immutable response for
// the threshold endpoints, or undefined for everything else (normal flow).
// reset returns only {id}; the editor re-fetches after a reset, so it just
// passes through.
API.prototype._realThresholdCall = async function ({
    endpoint,
    method,
    options = {},
}) {
    const action = THRESHOLD_ENDPOINTS[endpoint];
    if (!action) return undefined;

    const params = options.params || {};
    const scopeArgs = {
        truckId: params.truck_id || null,
        jobId: params.job_id || null,
    };

    // On write, map the FE category config → backend update shape.
    let realOptions = options;
    if (action === 'update') {
        const feConfig = options.data?.data ?? options.data;
        realOptions = {
            ...options,
            data: { data: adaptConfigForSave(feConfig) },
        };
    }

    const config = this._setConfig({ endpoint, method, options: realOptions });
    // Non-immutable so we can adapt the raw JSON before wrapping.
    const raw = await this._axios({ config, immutableResponse: false });
    if (!raw || raw.error) return raw;

    if (action === 'get' || action === 'update') {
        return Immutable.fromJS(adaptResolved(raw, scopeArgs));
    }
    if (action === 'legend') {
        return Immutable.fromJS(adaptLegend(raw));
    }
    return Immutable.fromJS(raw);
};

// main method for fetch data from an api that takes a config for axios
API.prototype.call = async function ({ endpoint, method, options }) {
    // call is for basic calls that don't need to update data in redux
    try {
        // QA-threshold endpoints are mapped through the FE↔BE adapter.
        const adapted = await this._realThresholdCall({
            endpoint,
            method,
            options,
        });
        if (adapted !== undefined) return adapted;
        const config = this._setConfig({ endpoint, method, options });
        return await this._axios({ config });
    } catch (error) {
        const message = 'Error caught in API_m/call';
        catchBlockErrors({ error, message });
    }
};

// Subscribe works almost the same as call except it takes a callbackAction to
// update the data in the redux store.
// the response from the backend should be setup so we can dispatch it as an
// action creator
API.prototype.subscribe = async function (callConfig, callbackAction) {
    try {
        const config = this._setConfig(callConfig);
        return await this._axios({ config, callbackAction });
    } catch (error) {
        const message = 'Error caught in API_m/subscribe';
        catchBlockErrors({ error, message });
    }
};

// when working with files, don't forget to pull the file out. it should be a
// binary in the payload.
API.prototype.filesUpload = async function ({
    endpoint,
    options = {},
    data = {},
    callbackAction = null,
}) {
    try {
        const apiMethod =
            typeof callbackAction === 'function' ? 'subscribe' : 'call';

        let filesToUpload = options?.files;
        if (filesToUpload && filesToUpload?.length) {
            options.data = new FormData();
            filesToUpload.forEach((file) => {
                options.data.append('file_objs', file);
            });
        }

        options.headers = {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${this.token}`,
        };
        delete options.files;

        return await this[apiMethod]({
            endpoint,
            method: 'post',
            options,
            callbackAction,
        });
    } catch (error) {
        const message = 'Error caught in API_m/fileUpload';
        catchBlockErrors({ error, message });
    }
};

API.prototype.fileUpload = async function ({
    endpoint,
    options = {},
    data = {},
    callbackAction = null,
}) {
    try {
        const apiMethod =
            typeof callbackAction === 'function' ? 'subscribe' : 'call';
        const fileToUpload = options.file;

        options.data = new FormData();
        options.data.append('file', fileToUpload);
        for (const key in data) {
            options.data.append(key, data[key]);
        }
        options.headers = {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${this.token}`,
        };
        delete options.file;

        return await this[apiMethod]({
            endpoint,
            method: 'post',
            options,
            callbackAction,
        });
    } catch (error) {
        const message = 'Error caught in API_m/fileUpload';
        catchBlockErrors({ error, message });
    }
};

// For uploading images va FormData
API.prototype.photoUpload = async function ({
    endpoint,
    options = {},
    data,
    callbackAction = null,
}) {
    try {
        const apiMethod =
            typeof callbackAction === 'function' ? 'subscribe' : 'call';
        options.data = new FormData();
        data.forEach((d) => options.data.append(d.key, d.value));
        return await this[apiMethod]({
            endpoint,
            method: 'post',
            options,
            callbackAction,
        });
    } catch (error) {
        const message = 'Error caught in API_m/photoUpload';
        catchBlockErrors({ error, message });
    }
};

// For downloading files and getting the headers of the response back in
// addition to the data. the headers are usually where we will find the file
// type
API.prototype.fileDownload = async function (
    callConfig,
    callbackAction = null,
) {
    try {
        const config = this._setConfig(callConfig);
        return await axios(config)
            .then((response) => {
                return { response: response };
            })
            .catch((error) => this._handleError({ error, callbackAction }));
    } catch (error) {
        const message = 'Error caught in API/fileDownload';
        catchBlockErrors({ error, message });
    }
};

API.prototype.nonIM = async function (callConfig, callbackAction) {
    try {
        const config = this._setConfig(callConfig);
        return await this._axios({
            config,
            callbackAction,
            immutableResponse: false,
        });
    } catch (error) {
        const message = 'Error caught in API_m/subscribe';
        catchBlockErrors({ error, message });
    }
};

API.prototype.getToken = function () {
    if (this.token) return this.token;
    else return false;
};

API.prototype._setConfig = function ({ endpoint, method, options }) {
    // options are any other property we want pass axios, params, data, callback
    // functions, anything.
    // options are {key: value}, key being the property key for axios, ie data
    // so example.
    // options: {
    //      data: {airportID, workOrderID},
    //      onUploadProgress: uploadProgressCallback
    //  }
    //
    try {
        const config = {
            url: endpoint,
            method: method,
            baseURL: baseURL,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this.token}`,
            },
        };

        // loop through our options and set them as properties
        for (const key in options) config[key] = options[key];

        return config;
    } catch (error) {
        const message = 'Error caught in API_m/_setConfig';
        catchBlockErrors({ error, message });
    }
};

// callbackAction is a redux action update passed in when we call subscribe
// we default it to null so we don't have to call it when it isn't avaliable
API.prototype._axios = async function ({
    config,
    callbackAction = null,
    immutableResponse = true,
}) {
    try {
        return await axios(config)
            .then((response) => {
                return this._handleSuccess({
                    data: response.data,
                    callbackAction,
                    immutableResponse,
                });
            })
            .catch((error) => this._handleError({ error, callbackAction }));
    } catch (error) {
        const message = 'Error caught in API_m/_axios';
        catchBlockErrors({ error, message });
    }
};

API.prototype._displaySnackBarFeedback = function (notification) {
    this.dispatch(showSnackbar(notification));
};

API.prototype._handleSuccess = async function ({
    data,
    callbackAction,
    immutableResponse,
}) {
    try {
        // if a callbackFunction was passed the it is a dispatchable action creator
        // for the redux store
        // we also turn the data into an immutable record here so if for cases
        // where we actually don't want immtuable data structor, use call then
        // dispatch an action with the response if it needs to be saved in
        // redux.
        // an example of that situation would be main and sub features, these
        // objects are much too large to turn immutable
        // responses are also shaped like data: data so if we want to set the
        // data in redux from here we need to pull it out. the check is just
        // in case some endpoints aren't shaped correctly
        const _data = data.data ? data.data : data;
        const responseData = immutableResponse
            ? Immutable.fromJS(_data)
            : _data;

        if (typeof callbackAction === 'function') {
            this.dispatch(callbackAction(responseData));
        }
        // we can can drop the callbackAction
        // if the backend returns a action object like say
        // data.action_creator : {type: 'assess/updateKeyValuePair', key, value };
        // we could do
        if (data.message && data.meessage !== null) {
            this._displaySnackBarFeedback({
                message: data.message,
                variant: 'success',
            });
        }
        return responseData;
    } catch (error) {
        const message = 'Error caught in API_m/_handleSuccess';
        catchBlockErrors({ error, message });
    }
};

API.prototype._handleError = async function ({ error, callbackAction }) {
    try {
        // if there is an error message from the backend pull it out and report it
        // to sentry
        // const _error = error.response && error.response.data ? error.response.data : error;
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        // if(error.response && error.response.user_feedback){

        if (error.response) {
            const immutableError = Immutable.fromJS(error.response);
            console.log('immutableError', immutableError.toJS());
            const userFeedback = immutableError.getIn(
                ['data', 'message'],
                null,
            );

            const errorCode = immutableError.getIn(
                ['data', 'detail', 'code'],
                null,
            );
            // if token expired redirect them back to the login page
            if (errorCode !== null && errorCode === 'token_expired') {
                window.location.reload();
                return { error: error };
            }

            if (userFeedback && typeof userFeedback === 'string') {
                this._displaySnackBarFeedback({
                    message: userFeedback,
                    variant: 'error',
                    persist: true,
                });
            }

            reportAPIErrorToSentry({
                backendResponse: immutableError.toJS(),
                category: error.config.method,
                token: this.token,
                error,
            });

            // we return an {error: error} structure so we can easily check for
            // apiResponse.error to see if an error happend from a component
            return { error: error };
        } else {
            // the call was dropped or for some reason there was no respons
            this._displaySnackBarFeedback({
                message: 'Error fetching requested information',
                variant: 'error',
                // persist: true
            });
            return { error: error };
        }
    } catch (error) {
        const message = 'Error caught in API_m/_handleError';
        catchBlockErrors({ error, message });
    }
};

API.prototype._repeatCall = async function ({ error }) {
    try {
        // check if we are online, and repeate until we have connection again.
        this.runningRepeat = true;
        let isOnline = navigator.onLine;
        if (!isOnline)
            setTimeout(async () => await this._repeatCall({ error }), 500);
        else {
            const promises = this.pendingCalls.map(async (error) => {
                const config = error.config;
                const callbackAction = error.callbackAction;
                await this._axios({ config, callbackAction }).finally(() => {
                    this.dispatch({
                        type: 'app/updatePendingCallsArray',
                        error,
                    });
                    this._updatePendingCallsArray(error);
                });
            });
            this.runningRepeat = false;
            return Promise.all(promises);
        }
    } catch (error) {
        const message = 'Error caught in API_m/_repeatCall';
        catchBlockErrors({ error, message });
    }
};

API.prototype._updatePendingCallsArray = function (error) {
    try {
        let updateMethod;
        let argForMethod = error;
        if (!this.pendingCalls.includes(error)) updateMethod = 'push';
        else {
            updateMethod = 'delete';
            argForMethod = this.pendingCalls.indexOf(error);
        }

        this.pendingCalls = this.pendingCalls[updateMethod](argForMethod);
    } catch (error) {
        const message = 'Error caught in API_m/_updatePendingCallsArray';
        catchBlockErrors({ error, message });
    }
};

API.prototype.mock = async function (options, asyncCallback) {
    let result;

    try {
        result = await asyncCallback(options);
    } catch (error) {
        result = { error: new Error('Mocked Error') };
    }

    return Immutable.fromJS(result);
};

API.prototype.mockSuccess = async function (options, responseData) {
    return this.mock(options, async (options) => responseData);
};

API.prototype.mockSuccessDelay = async function (
    options,
    responseData,
    delayMs = 1000,
) {
    return this.mock(
        options,
        async (options) =>
            new Promise((resolve, reject) => {
                setTimeout(() => {
                    resolve(responseData);
                }, delayMs);
            }),
    );
};

API.prototype.mockError = async function (options, responseData) {
    return this.mock(
        options,
        async (options) =>
            new Promise((resolve, reject) => {
                reject(responseData);
            }),
    );
};

API.prototype.mockErrorDelay = async function (
    options,
    responseData,
    delayMs = 1000,
) {
    return this.mock(
        options,
        async (options) =>
            new Promise((resolve, reject) => {
                setTimeout(() => {
                    reject(responseData);
                }, delayMs);
            }),
    );
};

export default API;
